import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { OnboardOrganizationDocument } from '@graphql/types/documents';
import { removeEmptyValues } from '@shared/utils/removeEmpty';
import { useCallback } from 'react';

import type { OnboardOrganizationInput, OrgResponse } from '../types';

export const useOnboardOrg = () => {
  const [onboardOrgMutation, { loading: isLoading }] = useMutation(OnboardOrganizationDocument);

  const doOnboardOrg = useCallback(
    async (data: OnboardOrganizationInput): Promise<OrgResponse> => {
      // Extract primaryAccount before cleaning to preserve it
      const { primaryAccount, ...restData } = data;

      // Only apply removeEmptyValues to non-primaryAccount fields
      const cleanedData = removeEmptyValues(restData) as Partial<OnboardOrganizationInput>;

      // Reconstruct input with original primaryAccount (required field, should not be cleaned)
      const inputData: OnboardOrganizationInput = {
        ...cleanedData,
        name: data.name, // Required field
        email: data.email, // Required field
        phoneNumber: data.phoneNumber, // Required field
        address: data.address, // Required field (Map type)
        primaryAccount, // Keep primaryAccount as-is (required field with all its properties)
      };

      const response = await onboardOrgMutation({
        variables: { input: inputData },
      });
      const organizationRes = response.data?.onboardOrganization;

      if (!organizationRes) {
        throw new Error('Organization creation failed');
      }

      // Only show success if organization was actually created (no errors)
      if (organizationRes.organization && !organizationRes.errors?.length) {
        notify.success({ title: 'Organization onboarded successfully' });
      }

      return organizationRes as OrgResponse;
    },
    [onboardOrgMutation]
  );

  return { doOnboardOrg, isLoading };
};
